import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { KoobButton } from '@koob/ui';
import {
  DesignTripWizard,
  TripHeader,
  TripVersionSelector,
  useListMode,
  useSetListMode,
  useTripFilters,
} from '@koob/trip-designer';
import { useApp, useLanguage, useResetTripDesigner } from '../../../hooks';
import { formatCurrency } from '../../../utils';
import VersionExportButton from './VersionExportButton';

// KD-3391: the trip header portals itself into the navbar slot; the
// list/map/itinerary switch lives in the compose panel.
const ComposeHeader = ({ tripId, versions, currentVersion, reloadTrip }) => {
  const { t } = useTranslation('tripDesigner');
  const { setLang } = useLanguage();
  const { currentUserLang } = useApp();
  const navigate = useNavigate();
  const resetContext = useResetTripDesigner();
  const tripFilters = useTripFilters();
  const listMode = useListMode();
  const setListMode = useSetListMode();
  const [wizardIsShown, setWizardIsShown] = useState(false);

  const toggleWizard = () => setWizardIsShown(shown => !shown);

  const exitTripDesigner = () => {
    resetContext();
    setLang(currentUserLang);
    navigate('/');
  };

  return (
    <>
      {wizardIsShown && (
        <DesignTripWizard
          mode="edit"
          onDone={toggleWizard}
          onExit={toggleWizard}
        />
      )}

      <TripHeader
        onTripEdit={toggleWizard}
        onExit={exitTripDesigner}
        versionSelector={
          tripFilters?.clientFolder?.id ? (
            <TripVersionSelector
              tripId={tripId}
              currentVersion={currentVersion}
              versions={versions}
              reloadTrip={reloadTrip}
              translation={t}
              renderVersionActions={version => (
                <VersionExportButton tripId={tripId} versionId={version.id} />
              )}
            />
          ) : undefined
        }
        extraActions={
          <KoobButton
            variant="primary"
            data-testid="conversation-mode"
            onClick={() => setListMode(listMode === 'chat' ? 'list' : 'chat')}
          >
            <i className="fa-light fa-messages" />
            {t('chatWithMyDmc')}
          </KoobButton>
        }
        tripLabel={t('currentTrip')}
        editLabel={t('update')}
        priceLabel={t('from')}
        paxLabel={t('pax')}
        misMatchCurrencyLabel={t('misMatchCurrency')}
        totalDetailsLabel={t('total_details')}
        pricePerPaxNotice={t('compose.pricePerPaxNotice')}
        exitLabel={t('exit')}
        exitUnsavedTitle={t('compose.exitUnsaved.title')}
        exitUnsavedDescription={t('compose.exitUnsaved.description')}
        exitUnsavedStayLabel={t('compose.exitUnsaved.stay')}
        exitUnsavedConfirmLabel={t('compose.exitUnsaved.confirm')}
        formatPrice={(amount, currency) => formatCurrency({ amount, currency })}
      />
    </>
  );
};

export default ComposeHeader;
